
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// types
import { InvoiceNewServiceT, InvoiceT } from '@/core/types/orders.types'
import {
    AdditionalServicesTypeT,
    UpdateAdditionalServicesPayloadT,
} from '@/core/types/additional_services.types'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { additionalServices } from '@/core/realisations/additional_services.realisation'

// notifications
import useNotifications from '@/common/composable/useNotifications'

// validations
import useVuelidate from '@vuelidate/core'
import { integer, decimal } from '@vuelidate/validators'

// classes
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: {
        PageTitle,
        Loader,
        TheButton,
        TheInput,
        Checkbox,
    },
    setup() {
        const route = useRoute()
        const router = useRouter()

        const notifications = useNotifications()

        const invoice_id = Number(route.params.invoice_id as string)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            invoice: null as InvoiceT | null,
        })

        const data_state = reactive({
            additional_services: new DataState<AdditionalServicesTypeT>({}),
        })

        const additional_services_state = reactive({
            is_loading: false,
            //
            loadersCount: 0,
            manipulatorCount: 0,
            craneCount: 0,
            carCount: 0,
            hydraulicTrolleyCount: 0,
            palletCount: 0,
            softPackageCount: 0,
            gridCount: 0,
        })

        const additional_services_state_form$ = useVuelidate(
            {
                loadersCount: { integer },
                manipulatorCount: { integer },
                craneCount: { integer },
                carCount: { integer },
                hydraulicTrolleyCount: { decimal },
                palletCount: { decimal },
                softPackageCount: { decimal },
                gridCount: { decimal },
            },
            additional_services_state
        )

        const loadAdditionalServices = async () => {
            data_state.additional_services.pagination.current_page = 1

            try {
                data_state.additional_services.is_loading = true

                const response = await additionalServices.getList()
                data_state.additional_services.list = response.data

                data_state.additional_services.pagination.total_pages = 1

                data_state.additional_services.is_failed = false
                data_state.additional_services.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.additional_services.failure.catchError(error)

                if (!data_state.additional_services.failure.error_state.is_cancel) {
                    data_state.additional_services.is_failed = true
                }
            } finally {
                if (!data_state.additional_services.failure.error_state.is_cancel) {
                    data_state.additional_services.is_loading = false
                }
            }
        }

        // const initializeOldAdditionalServices = () => {
        //     if (!state.invoice || !state.invoice.additional_services) return

        //     if (state.invoice.additional_services.loader) { additional_services_state.loadersCount = state.invoice.additional_services.amount_of_loaders }
        //     if (state.invoice.additional_services.soft_package) { additional_services_state.softPackageCount = 1 }
        //     if (state.invoice.additional_services.grid) { additional_services_state.gridCount = 1 }
        //     if (state.invoice.additional_services.manipulator) { additional_services_state.manipulatorCount = 1 }
        //     if (state.invoice.additional_services.crane) { additional_services_state.craneCount = 1 }
        //     if (state.invoice.additional_services.car) { additional_services_state.carCount = 1 }
        //     if (state.invoice.additional_services.hydraulic_trolley) { additional_services_state.hydraulicTrolleyCount = 1 }
        //     if (state.invoice.additional_services.pallet) { additional_services_state.palletCount = 1 }
        // }

        const getAdditionalService = (type_code: string): InvoiceNewServiceT | null => {
            const service = (state.invoice as InvoiceT).additional_services_values.find(
                (service) => service.type_code === type_code
            )
            if (!service) return null

            return service
        }

        const initializeNewAdditionalServices = () => {
            const loaders_service = getAdditionalService('loader')
            const soft_package_service = getAdditionalService('soft_package')
            const grid_service = getAdditionalService('grid')
            const manipulator_service = getAdditionalService('manipulator')
            const crane_service = getAdditionalService('crane')
            const car_service = getAdditionalService('car')
            const hydraulic_trolley_service = getAdditionalService('hydraulic_trolley')
            const delivery_service = getAdditionalService('delivery')
            const pallet_service = getAdditionalService('pallet')

            if (loaders_service) {
                additional_services_state.loadersCount = loaders_service.value || 1
            }
            if (soft_package_service) {
                additional_services_state.softPackageCount = soft_package_service.value || 1
            }
            if (grid_service) {
                additional_services_state.gridCount = grid_service.value || 1
            }
            if (manipulator_service) {
                additional_services_state.manipulatorCount = manipulator_service.value || 1
            }
            if (crane_service) {
                additional_services_state.craneCount = crane_service.value || 1
            }
            if (car_service) {
                additional_services_state.carCount = car_service.value || 1
            }
            if (hydraulic_trolley_service) {
                additional_services_state.hydraulicTrolleyCount =
                    hydraulic_trolley_service.value || 1
            }
            if (pallet_service) {
                additional_services_state.palletCount = pallet_service.value || 1
            }
        }

        const loadInvoice = async () => {
            try {
                state.is_loading = true

                const response = await orders.getInvoice(invoice_id)
                state.invoice = response.data

                if (state.invoice.additional_services_values.length > 0) {
                    initializeNewAdditionalServices()
                }

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении накладной произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadInvoice()
            loadAdditionalServices()
        })

        const getAdditionalServiceTypeId = (code: string): number | null => {
            const service = data_state.additional_services.list.find(
                (additional_service) => additional_service.code === code
            )
            if (!service) return null

            return service.id
        }

        const getPayload = (): UpdateAdditionalServicesPayloadT => {
            const additional_service_values = [] as {
                type_id: number
                value: number
                duration: number
            }[]

            if (additional_services_state.carCount > 0) {
                const car_type_id = getAdditionalServiceTypeId('car')

                if (car_type_id) {
                    additional_service_values.push({
                        type_id: car_type_id,
                        value: Number(additional_services_state.carCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.craneCount > 0) {
                const crane_type_id = getAdditionalServiceTypeId('crane')

                if (crane_type_id) {
                    additional_service_values.push({
                        type_id: crane_type_id,
                        value: Number(additional_services_state.craneCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.gridCount > 0) {
                const grid_type_id = getAdditionalServiceTypeId('grid')

                if (grid_type_id) {
                    additional_service_values.push({
                        type_id: grid_type_id,
                        value: Number(additional_services_state.gridCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.hydraulicTrolleyCount > 0) {
                const hydraulic_trolley_type_id = getAdditionalServiceTypeId('hydraulic_trolley')

                if (hydraulic_trolley_type_id) {
                    additional_service_values.push({
                        type_id: hydraulic_trolley_type_id,
                        value: Number(additional_services_state.hydraulicTrolleyCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.loadersCount > 0) {
                const loader_type_id = getAdditionalServiceTypeId('loader')

                if (loader_type_id) {
                    additional_service_values.push({
                        type_id: loader_type_id,
                        value: Number(additional_services_state.loadersCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.manipulatorCount > 0) {
                const manipulator_type_id = getAdditionalServiceTypeId('manipulator')

                if (manipulator_type_id) {
                    additional_service_values.push({
                        type_id: manipulator_type_id,
                        value: Number(additional_services_state.manipulatorCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.palletCount > 0) {
                const pallet_type_id = getAdditionalServiceTypeId('pallet')

                if (pallet_type_id) {
                    additional_service_values.push({
                        type_id: pallet_type_id,
                        value: Number(additional_services_state.palletCount),
                        duration: 1,
                    })
                }
            }

            if (additional_services_state.softPackageCount > 0) {
                const soft_package_type_id = getAdditionalServiceTypeId('soft_package')

                if (soft_package_type_id) {
                    additional_service_values.push({
                        type_id: soft_package_type_id,
                        value: Number(additional_services_state.softPackageCount),
                        duration: 1,
                    })
                }
            }

            return {
                additional_service_values,
            }
        }

        const updateAdditionalServices = async () => {
            if (additional_services_state.is_loading) return

            additional_services_state_form$.value.$touch()
            if (additional_services_state_form$.value.$invalid) return

            try {
                additional_services_state.is_loading = true

                const payload = getPayload()
                await additionalServices.updateInvoiceAdditionalServices(invoice_id, payload)

                notifications.notify({ title: 'Доп. услуги сохранены', type: 'success' })
                router.push(`/invoice/${invoice_id}`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении доп. услуг произошла ошибка',
                })
            } finally {
                additional_services_state.is_loading = false
            }
        }

        const pageTitle = computed(() => {
            if (!state.invoice) return 'Редактирование доп. услуг накладной'
            return `Редактирование доп. услуг накладной ${state.invoice.invoice_number}`
        })

        return {
            state,
            data_state,
            additional_services_state,
            additional_services_state_form$,
            pageTitle,
            loadInvoice,
            updateAdditionalServices,
        }
    },
})
